import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Field } from "@/components/common/bits";
import { ThemeToggle } from "@/components/layout/AppShell";
import { useApi } from "@/hooks/useApi";
import { startSso } from "@/pages/SsoCallback";

function SsoButtons() {
  const { data: providers } = useApi("/auth/sso/providers");
  if (!providers) return null;
  return (
    <div className="pt-1">
      <div className="flex items-center gap-3 text-[11px] text-muted-foreground my-3"><span className="flex-1 h-px bg-border" />or continue with<span className="flex-1 h-px bg-border" /></div>
      <div className="grid grid-cols-2 gap-2">
        {providers.map((p) => <Button key={p.code} variant="outline" disabled={!p.configured} title={p.configured ? "" : `Set ${p.env.join(", ")} in backend/.env`} onClick={() => startSso(p.code).catch((e) => toast.error(errMsg(e)))} data-testid={`sso-${p.code}`}>{p.label}{!p.configured && <span className="ml-1.5 text-[10px] text-muted-foreground">(not configured)</span>}</Button>)}
      </div>
    </div>
  );
}

const DEMO = [["Consultant (4 clients)", "consultant@demo.complykosh.com"], ["Compliance owner · IT", "priya.owner@nimbus-demo.in"], ["CISO · Healthcare/fintech IT", "neha.ciso@medpay-demo.in"], ["Auditor (read-only)", "auditor@demo.complykosh.com"]];

export default function Login() {
  const { applySession } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [login, setLogin] = useState({ email: params.get("email") || "", password: params.get("email") ? "Demo@12345" : "" });
  const [reg, setReg] = useState({ name: "", email: "", password: "", org_name: "", sector: "it_services", account_type: "org" });
  const [busy, setBusy] = useState(false);
  const [otp, setOtp] = useState(null);
  const [code, setCode] = useState("");

  const finish = (data) => { applySession(data); toast.success("Welcome to ComplyKosh"); navigate("/app"); };
  const verifyOtp = async () => {
    setBusy(true);
    try { const r = await api.post("/auth/2fa/verify", { challenge_id: otp.challenge_id, code }); finish(r.data); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const submit = async (path, body) => {
    setBusy(true);
    try {
      const r = await api.post(path, body);
      if (r.data.requires_2fa) { setOtp(r.data); return; }
      finish(r.data);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr]">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-slate-950 text-slate-100 relative overflow-hidden">
        <div className="absolute inset-0 opacity-40" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, rgba(59,130,246,.35), transparent 45%), radial-gradient(circle at 80% 70%, rgba(20,184,166,.3), transparent 45%)" }} />
        <div className="relative flex items-center gap-2"><Link to="/" className="flex items-center gap-2" data-testid="login-home-link"><div className="h-9 w-9 rounded-md bg-blue-500 grid place-items-center font-display font-extrabold">C</div><span className="font-display font-bold text-xl">ComplyKosh</span></Link></div>
        <div className="relative max-w-lg">
          <div className="eyebrow text-blue-300 mb-4">One control library · many frameworks</div>
          <h1 className="font-display text-4xl font-extrabold tracking-tight leading-tight">ISO 27001, SOC 2, ISO 9001, DPDP, HIPAA, PCI-DSS, NIST CSF, ISO 14001/45001 and NAAC — handled from one workspace.</h1>
          <p className="mt-5 text-slate-300 leading-relaxed">Built for IT firms, manufacturers, colleges and the consultants who serve them. Works without cloud integrations: evidence, registers, audits and CAPA first.</p>
          <div className="mt-8 grid grid-cols-3 gap-4 text-sm">
            {[["75", "cross-mapped controls"], ["36", "policy templates"], ["9", "frameworks + NAAC pack"]].map(([v, l]) => <div key={l} className="border border-slate-800 rounded-lg p-4 bg-slate-900/60"><div className="font-display text-2xl font-bold">{v}</div><div className="text-slate-400 text-xs mt-1">{l}</div></div>)}
          </div>
        </div>
        <div className="relative text-xs text-slate-500 flex items-center gap-2"><ShieldCheck size={14} /> India-priced, globally usable. Consultant &amp; auditor native.</div>
      </div>
      <div className="flex flex-col p-6 sm:p-12">
        <div className="flex justify-end"><ThemeToggle /></div>
        <div className="flex-1 flex items-center">
          <div className="w-full max-w-md mx-auto fade-up">
            {otp ? (
              <div data-testid="otp-step">
                <h2 className="font-display text-2xl font-bold">Check your email</h2>
                <p className="text-muted-foreground mt-1 mb-6">We sent a 6-digit code to {otp.email_hint}. It expires in 10 minutes.</p>
                <Field label="One-time code"><Input data-testid="otp-code" inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} onKeyDown={(e) => e.key === "Enter" && verifyOtp()} className="text-center text-2xl tracking-[0.5em] font-mono" /></Field>
                <Button className="w-full mt-4" disabled={busy || code.length !== 6} onClick={verifyOtp} data-testid="otp-submit">Verify & sign in</Button>
                <button className="text-xs text-muted-foreground hover:underline mt-3" onClick={() => { setOtp(null); setCode(""); }} data-testid="otp-back">Back to login</button>
              </div>
            ) : (<>
            <h2 className="font-display text-2xl font-bold">Sign in to your workspace</h2>
            <p className="text-muted-foreground mt-1 mb-6">Use a demo account or create your own organisation.</p>
            <Tabs defaultValue={params.get("tab") === "register" ? "register" : "login"}>
              <TabsList className="grid grid-cols-2 w-full"><TabsTrigger value="login" data-testid="tab-login">Log in</TabsTrigger><TabsTrigger value="register" data-testid="tab-register">Create account</TabsTrigger></TabsList>
              <TabsContent value="login" className="space-y-4 pt-4">
                <Field label="Email"><Input data-testid="login-email" type="email" value={login.email} onChange={(e) => setLogin({ ...login, email: e.target.value })} placeholder="you@company.com" /></Field>
                <Field label="Password"><Input data-testid="login-password" type="password" value={login.password} onChange={(e) => setLogin({ ...login, password: e.target.value })} onKeyDown={(e) => e.key === "Enter" && submit("/auth/login", login)} /></Field>
                <Button className="w-full" disabled={busy} onClick={() => submit("/auth/login", login)} data-testid="login-submit">Log in</Button>
                <SsoButtons />
                <div className="pt-2">
                  <div className="eyebrow mb-2">Demo accounts · password Demo@12345</div>
                  <div className="grid gap-1.5">
                    {DEMO.map(([label, email]) => (
                      <button key={email} className="text-left text-xs border rounded-md px-3 py-2 row-hover flex justify-between" onClick={() => setLogin({ email, password: "Demo@12345" })} data-testid={`demo-${email.split("@")[0]}`}>
                        <span className="font-medium">{label}</span><span className="text-muted-foreground font-mono">{email}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </TabsContent>
              <TabsContent value="register" className="space-y-4 pt-4">
                <Field label="Account type">
                  <Select value={reg.account_type} onValueChange={(v) => setReg({ ...reg, account_type: v })}>
                    <SelectTrigger data-testid="register-account-type"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="org">Organisation (self-serve)</SelectItem><SelectItem value="consultant">Consultant / audit firm (multi-client)</SelectItem></SelectContent>
                  </Select>
                </Field>
                <Field label="Your name"><Input data-testid="register-name" value={reg.name} onChange={(e) => setReg({ ...reg, name: e.target.value })} /></Field>
                <Field label="Email"><Input data-testid="register-email" type="email" value={reg.email} onChange={(e) => setReg({ ...reg, email: e.target.value })} /></Field>
                <Field label="Password (min 8 chars)"><Input data-testid="register-password" type="password" value={reg.password} onChange={(e) => setReg({ ...reg, password: e.target.value })} /></Field>
                {reg.account_type === "org" && (
                  <>
                    <Field label="Organisation name"><Input data-testid="register-org-name" value={reg.org_name} onChange={(e) => setReg({ ...reg, org_name: e.target.value })} /></Field>
                    <Field label="Sector">
                      <Select value={reg.sector} onValueChange={(v) => setReg({ ...reg, sector: v })}>
                        <SelectTrigger data-testid="register-sector"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="it_services">IT services / SaaS</SelectItem><SelectItem value="manufacturing">Manufacturing</SelectItem><SelectItem value="automotive">Automotive supplier</SelectItem><SelectItem value="aerospace">Aerospace supplier</SelectItem>
                          <SelectItem value="college">College / University</SelectItem><SelectItem value="school">School</SelectItem><SelectItem value="other">Other SMB</SelectItem>
                        </SelectContent>
                      </Select>
                    </Field>
                  </>
                )}
                <Button className="w-full" disabled={busy} onClick={() => submit("/auth/register", reg)} data-testid="register-submit">Create account</Button>
              </TabsContent>
            </Tabs>
            </>)}
          </div>
        </div>
      </div>
    </div>
  );
}
